import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Contact Details — Official Email & Enquiries";
const DESC =
  "Raj Shamani's official contact details: the partnerships email, how to send brand, speaking, podcast-guest and media enquiries, and what actually gets a reply.";
const URL = "https://rajshamani.life/raj-shamani-contact-details";
const OG = OG_ABOUT;
const EMAIL = "partnerships@figuringout.co";

const channels = [
  {
    name: "Brand partnerships",
    detail: `Email ${EMAIL} with the brand, budget range, deliverables and timeline in the first message.`,
  },
  {
    name: "Speaking enquiries",
    detail:
      "Send the event date, city, audience size and topic. Keynote topics and past stages are listed on the speaker page.",
  },
  {
    name: "Podcast guest pitches",
    detail:
      "Use the guest form rather than email — it asks for the specific questions the selection process needs.",
  },
  {
    name: "Press & media",
    detail:
      "Email with the outlet, format, deadline and whether it is written, audio or on-camera.",
  },
];

const faq = [
  {
    q: "What is Raj Shamani's official email address?",
    a: `Business, partnership, speaking and media enquiries go to ${EMAIL}. It is the only email address published on this site.`,
  },
  {
    q: "How do I contact Raj Shamani directly?",
    a: "Use the contact form on this site or the partnerships email. Instagram and LinkedIn DMs are rarely read because of the volume he receives.",
  },
  {
    q: "What is Raj Shamani's phone number?",
    a: "No personal phone number is published. Any site or account claiming to sell his number or WhatsApp contact is not legitimate.",
  },
  {
    q: "How do I book Raj Shamani for an event?",
    a: "Send the event date, city, audience and topic to the partnerships email, or use the speaker page which explains fit and format before you write.",
  },
  {
    q: "How do I get on the Figuring Out podcast?",
    a: "Pitch through the guest page. Selection is based on the story and evidence you bring, not on follower count.",
  },
  {
    q: "How long does a reply take?",
    a: "Genuine enquiries with full details are answered fastest. Vague one-line messages without a brand, budget or date usually go unanswered.",
  },
];

export const Route = createFileRoute("/raj-shamani-contact-details")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani official contact details", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ContactDetailsPage,
});

function ContactDetailsPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Contact Details" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Mail size={14} /> Official contact
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">contact details</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            The official email address is{" "}
            <a
              href={`mailto:${EMAIL}`}
              className="text-gold underline underline-offset-4 font-semibold"
            >
              {EMAIL}
            </a>
            . It handles brand partnerships, speaking enquiries and press. No personal phone number
            is published, and DMs are rarely read.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Which channel to use</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {channels.map((c) => (
            <div key={c.name} className="p-6 rounded-xl border border-border bg-card">
              <div className="text-xs uppercase tracking-widest text-gold mb-2">{c.name}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.detail}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground max-w-3xl leading-relaxed">
          The fastest route for most people is the{" "}
          <Link to="/contact" className="text-gold underline underline-offset-4">
            contact form
          </Link>
          . For events, read{" "}
          <Link to="/raj-shamani-speaker" className="text-gold underline underline-offset-4">
            the speaker page
          </Link>{" "}
          first; to pitch yourself as a guest, use the{" "}
          <Link to="/guest" className="text-gold underline underline-offset-4">
            guest application
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-contact-details" />
      <RelatedPages pages={["aion", "about", "faq", "podcast", "ventures", "book"]} />
    </div>
  );
}
